import autobind from "autobind-decorator";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import React from "react";
import type { ColProps } from "react-bootstrap";
import { Col, Row } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import type { FormItemProps } from "./FormItem";
import { FormItem } from "./FormItem";
import type { FormRadioOption, FormRadioOptions } from "./FormRadio";

export interface RadioBtnButtonProps<T> extends FormItemProps<T> {
	options: FormRadioOptions<T>;
	variant?: string;
	inProgress?: boolean;
	outerColPros?: ColProps;
}

export class RadioBtnButton<T = string> extends FormItem<T, RadioBtnButtonProps<T>> {
	protected renderField(fieldProps: FieldRenderProps<T>): ReactNode {
		return <div>{this.renderOptions(fieldProps)}</div>;
	}

	protected renderOptions(fieldProps: FieldRenderProps<T>): ReactNode {
		const { options, outerColPros } = this.props;

		if (outerColPros) {
			return (
				<Row>
					{options.map((option: FormRadioOption<T>, key) => (
						<Col key={key} {...outerColPros}>
							{this.renderRadio(option, key, fieldProps)}
						</Col>
					))}
				</Row>
			);
		}

		return options.map((option: FormRadioOption<T>, key) => this.renderRadio(option, key, fieldProps));
	}

	protected renderRadio(option: FormRadioOption<T>, key: number, fieldProps: FieldRenderProps<T>): ReactNode {
		const { readOnly, disabled, tabIndex, variant = "primary", options, inProgress, outerColPros } = this.props;
		const {
			input: { name, value },
		} = fieldProps;
		const checked = value === option.value;
		const className = compact([
			`btn btn-outline-${variant}`,
			checked && "active",
			key !== options.length - 1 && "mr-2",
			checked && inProgress && "btn-progress",
			outerColPros && "btn-block",
		]).join(" ");

		return (
			<label key={key} htmlFor={option.id || `${this.id}-${key}`} className={className}>
				<input
					type="radio"
					style={{ clip: "rect(0,0,0,0)", position: "absolute" }}
					id={option.id || `${this.id}-${key}`}
					name={name}
					tabIndex={tabIndex}
					value={String(option.value)}
					checked={checked}
					readOnly={readOnly}
					disabled={disabled || readOnly || inProgress}
					onChange={() => {
						this.handleChange(option.value, fieldProps);
					}}
					onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
						this.handleFocus(event, fieldProps);
					}}
					onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
						this.handleBlur(event, fieldProps);
					}}
				/>
				{option.label}
			</label>
		);
	}

	@autobind
	protected handleValidate(value: T, allValues: Record<string, any>): string {
		const { validate, required } = this.props;

		if (this.isDisabled()) {
			return undefined;
		}

		if (validate) {
			return validate(value, allValues);
		}

		if (
			required &&
			((typeof value !== "boolean" && !value) || (typeof value === "boolean" && value === undefined))
		) {
			return "Kérjük válassz ki egy elemet!";
		}
	}

	public static stringArrayToOptions(source: Array<string>): FormRadioOptions<string> {
		return source.map(
			(option: string) =>
				({
					label: option,
					value: option,
				}) as FormRadioOption<string>,
		);
	}
}
